import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { notifyError } from '../notify';
import { getFriendlyAuthErrorMessage } from '../authErrors';
import { writeAuditLogSafe } from '../services/auditLogService';

import { auth } from '../firebase';
import { API_BASE_URL } from '../config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const verifyInFlightRef = useRef(null);
  const loginAttemptRef = useRef(false);
  const unloadTokenRef = useRef(null);
  const offlineSyncSentRef = useRef(false);

  const roleToPermissions = (role) => {
    if (role === 'system_admin') {
      return [
        'view_dashboard',
        'manage_users',
        'view_documents',
        'system_settings',
        'manage_admins',
        'view_audit_logs',
      ];
    }
    if (role === 'admin') {
      return ['view_dashboard', 'manage_users', 'view_documents'];
    }
    return [];
  };

  const getActorFromAppUser = (appUser) => ({
    uid: appUser?.uid || auth.currentUser?.uid || null,
    email: appUser?.email || auth.currentUser?.email || 'unknown',
    role: appUser?.role || 'unknown',
    name: appUser?.name || appUser?.profile?.name || null,
    username: appUser?.username || appUser?.profile?.username || null,
    isAuthenticated: !!(appUser?.uid || auth.currentUser?.uid),
  });

  const verifyWithBackend = async (firebaseUser) => {
    // Prevent duplicate concurrent verifications.
    if (verifyInFlightRef.current) {
      return verifyInFlightRef.current;
    }

    const promise = (async () => {
      const idToken = await firebaseUser.getIdToken(true);
      const resp = await fetch(`${API_BASE_URL}/api/auth/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({}),
      });

      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || !payload.authorized) {
        const message = payload?.error || 'Unauthorized';
        const err = new Error(message);
        err.code = 'AUTH_UNAUTHORIZED';
        throw err;
      }
      return payload;
    })();

    verifyInFlightRef.current = promise;
    try {
      return await promise;
    } finally {
      verifyInFlightRef.current = null;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (!firebaseUser) {
          unloadTokenRef.current = null;
          localStorage.removeItem('user');
          setUser(null);
          return;
        }

        // Validate sysadmin role securely before we set app state.
        const verified = await verifyWithBackend(firebaseUser);
        const appRole = verified?.appRole;
        const safeProfile = verified?.user || {};

        const subscriptionStatus = safeProfile.subscriptionStatus || 'active';
        const subscriptionTime = safeProfile.subscriptionTime || null;
        const isSubscriptionExpired = (() => {
          if (subscriptionStatus === 'expired') return true;
          if (subscriptionTime) {
            const expiry = new Date(subscriptionTime).getTime();
            if (!isNaN(expiry) && Date.now() > expiry) return true;
          }
          return false;
        })();

        const nextUser = {
          uid: safeProfile.uid || firebaseUser.uid,
          email: safeProfile.email || firebaseUser.email,
          name: safeProfile.name || null,
          username: safeProfile.username || null,
          profilePhoto: safeProfile.profilePhoto || null,
          role: appRole,
          adminaccessLevel: safeProfile.adminaccessLevel || 'none',
          subscriptionStatus,
          subscriptionTime,
          isSubscriptionExpired,
          location: safeProfile.location || {
            barangay: '',
            municipality: '',
            province: '',
            region: '',
          },
          permissions: roleToPermissions(appRole),
          profile: {
            name: safeProfile.name,
            username: safeProfile.username,
            profilePhoto: safeProfile.profilePhoto || null,
            roles: safeProfile.roles,
            isVerified: safeProfile.isVerified,
            isUserOnline: safeProfile.isUserOnline === true,
            docId: safeProfile.docId,
            adminaccessLevel: safeProfile.adminaccessLevel || 'none',
            subscriptionStatus,
            subscriptionTime,
            isSubscriptionExpired,
            location: safeProfile.location,
          },
        };

        localStorage.setItem('user', JSON.stringify(nextUser));
        setUser(nextUser);
      } catch (err) {
        // If role/doc/verification fails, ensure the Firebase session is cleared.
        await writeAuditLogSafe({
          action: 'Session verification failed',
          details: err?.message || 'Role or backend verification failed during auth state check.',
          severity: 'Warning',
          category: 'authentication',
          outcome: 'failure',
          actor: getActorFromAppUser(null),
          metadata: {
            code: err?.code || null,
            source: 'onAuthStateChanged',
          },
          tags: ['auth', 'verification', 'session'],
        });

        localStorage.removeItem('user');
        setUser(null);
        if (!loginAttemptRef.current && err?.message) {
          notifyError(getFriendlyAuthErrorMessage(err));
        }
        try {
          await signOut(auth);
        } catch {
          // ignore
        }
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) {
      unloadTokenRef.current = null;
      return;
    }

    let isCancelled = false;

    const syncUnloadToken = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser || isCancelled) return;
        const idToken = await currentUser.getIdToken();
        if (!isCancelled && typeof idToken === 'string' && idToken.length > 0) {
          unloadTokenRef.current = idToken;
        }
      } catch {
        // Ignore token refresh failures; unload sync is best-effort.
      }
    };

    syncUnloadToken();
    const tokenRefreshTimer = setInterval(syncUnloadToken, 4 * 60 * 1000);

    return () => {
      isCancelled = true;
      clearInterval(tokenRefreshTimer);
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;

    offlineSyncSentRef.current = false;

    const syncOfflineOnClose = () => {
      if (offlineSyncSentRef.current) return;

      const currentIdToken = unloadTokenRef.current || auth.currentUser?.accessToken;
      if (!currentIdToken) return;

      offlineSyncSentRef.current = true;
      const url = `${API_BASE_URL}/api/auth/offline`;
      const payload = JSON.stringify({ idToken: currentIdToken });

      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        const body = new Blob([payload], { type: 'application/json' });
        navigator.sendBeacon(url, body);
        return;
      }

      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: payload,
        keepalive: true,
      }).catch(() => {
        // Ignore best-effort offline sync failures during page close.
      });
    };

    window.addEventListener('pagehide', syncOfflineOnClose);
    window.addEventListener('beforeunload', syncOfflineOnClose);

    return () => {
      window.removeEventListener('pagehide', syncOfflineOnClose);
      window.removeEventListener('beforeunload', syncOfflineOnClose);
    };
  }, [user]);

  const login = async (email, password) => {
    loginAttemptRef.current = true;
    let authCred = null;

    try {
      authCred = await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      await writeAuditLogSafe({
        action: 'Login attempt failed',
        details: err?.message || 'Firebase sign-in request failed.',
        severity: 'Warning',
        category: 'authentication',
        outcome: 'failure',
        actor: {
          uid: null,
          email: email || 'unknown',
          role: 'unknown',
          name: null,
          username: null,
          isAuthenticated: false,
        },
        metadata: {
          code: err?.code || null,
          stage: 'firebase_sign_in',
        },
        tags: ['auth', 'login', 'failed'],
      });
      loginAttemptRef.current = false;
      throw err;
    }

    try {
      if (!authCred || !authCred.user) {
        throw new Error('Authentication failed - no user credentials received.');
      }

      const verified = await verifyWithBackend(authCred.user);

      await writeAuditLogSafe({
        action: 'User login successful',
        details: `Authenticated as ${verified?.appRole || 'unknown role'} via backend verification.`,
        severity: 'Info',
        category: 'authentication',
        outcome: 'success',
        actor: {
          uid: authCred.user?.uid || null,
          email: authCred.user?.email || email || 'unknown',
          role: verified?.appRole || 'unknown',
          name: verified?.user?.name || null,
          username: verified?.user?.username || null,
          isAuthenticated: true,
        },
        metadata: {
          stage: 'backend_verification',
          authorized: true,
        },
        tags: ['auth', 'login', 'success'],
      });

      return { ok: true, role: verified?.appRole };
    } catch (err) {
      const actorInfo = {
        uid: authCred?.user?.uid || null,
        email: authCred?.user?.email || email || 'unknown',
        role: 'unknown',
        name: null,
        username: null,
        isAuthenticated: !!authCred?.user,
      };

      await writeAuditLogSafe({
        action: 'Login blocked after verification',
        details: err?.message || 'Backend authorization failed after Firebase sign-in.',
        severity: 'Warning',
        category: 'authentication',
        outcome: 'failure',
        actor: actorInfo,
        metadata: {
          code: err?.code || null,
          stage: 'backend_verification',
        },
        tags: ['auth', 'login', 'blocked'],
      });

      await signOut(auth);
      throw err;
    } finally {
      loginAttemptRef.current = false;
    }
  };

  const logout = async () => {
    const actor = getActorFromAppUser(user);

    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        const idToken = await currentUser.getIdToken();
        await fetch(`${API_BASE_URL}/api/auth/offline`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({}),
        }).catch(() => {
          // Ignore offline sync failures and continue local sign out.
        });
      }

      await writeAuditLogSafe({
        action: 'User logout successful',
        details: 'User session was signed out and offline sync endpoint was notified.',
        severity: 'Info',
        category: 'authentication',
        outcome: 'success',
        actor,
        metadata: {
          hadCurrentUser: !!currentUser,
        },
        tags: ['auth', 'logout', 'success'],
      });
    } catch {
      await writeAuditLogSafe({
        action: 'Logout encountered sync error',
        details: 'Offline sync failed while attempting to sign out, local sign-out continued.',
        severity: 'Warning',
        category: 'authentication',
        outcome: 'partial_success',
        actor,
        tags: ['auth', 'logout', 'warning'],
      });

      // Ignore offline sync failures and continue local sign out.
    }

    localStorage.removeItem('user');
    setUser(null);
    await signOut(auth);
  };

  const hasPermission = (permission) => {
    return user?.permissions?.includes(permission) || false;
  };

  const isAdmin = () => user?.role === 'admin';
  const isSystemAdmin = () => user?.role === 'system_admin';
  const isUser = () => user?.role === 'user';

  const getIdToken = async (forceRefresh = false) => {
    if (!auth.currentUser) return null;
    return await auth.currentUser.getIdToken(forceRefresh);
  };

  const updateProfile = (profileData) => {
    setUser(prev => {
      if (!prev) return null;
      const newUser = {
        ...prev,
        name: profileData.name !== undefined ? profileData.name : prev.name,
        username: profileData.username !== undefined ? profileData.username : prev.username,
        profilePhoto: profileData.profilePhoto !== undefined ? profileData.profilePhoto : prev.profilePhoto,
        profile: { 
          ...prev.profile, 
          name: profileData.name !== undefined ? profileData.name : prev.profile.name,
          username: profileData.username !== undefined ? profileData.username : prev.profile.username,
          profilePhoto: profileData.profilePhoto !== undefined ? profileData.profilePhoto : prev.profile.profilePhoto
        }
      };
      localStorage.setItem('user', JSON.stringify(newUser));
      return newUser;
    });
  };

  const value = {
    user,
    loading,
    login,
    logout,
    hasPermission,
    isAdmin,
    isSystemAdmin,
    isUser,
    updateProfile,
    getIdToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
